import type { VerticalLandingContent } from '@nexofacil/schemas';
import {
  ArrowRight,
  Award,
  Car,
  Check,
  CheckCircle2,
  Clock,
  Flame,
  Heart,
  Instagram,
  MapPin,
  MessageCircle,
  Sparkles,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { GetBrokerResponse, PublicBrokerSummary } from '@/lib/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { PropertyCard } from '@/components/property-card';
import { NoFilterMatch, PropertyFilterBar, usePropertyFilters } from '@/components/property-filters';
import { propertyStageLabel } from '@/lib/labels';
import { formatPhoneDisplay } from '@/lib/phone-format';
import { priceLabel } from '@/lib/property-display';
import { cn } from '@/lib/utils';
import type { LandingProps } from './types';

// =============================================================================
// DeibianeLanding · design dedicado pra Deibiane Cavalcante (broker MCMV).
//
// Estética elegante feminina baseada na identidade visual dela:
//   - Marsala (#610027) como cor de ação/destaque
//   - Algodão Egípcio (#F1EDE6) como fundo cremoso
//   - Fendi (#6E5B57) e Nude (#E3D9CD) como neutros de apoio
//   - Tipografia: Playfair Display (serif elegante) pra títulos +
//     Plus Jakarta Sans (default do app) pra UI
//
// Pedidos específicos da Deibiane (vs landing MCMV genérica):
//   - Sem badge "MCMV · análise gratuita" no hero (visualmente poluído)
//   - Mais respiro vertical, paleta cremosa em vez de navy denso
//   - Foco em "Quem vai te atender" como prova social (Top 3 Brasil MRV)
//
// Cores são literais (não via CSS var) porque essa landing é override por
// broker · paleta fixa, sem precisar de tema dinâmico.
// =============================================================================

const DEIBIANE_COLORS = {
  bgCream: '#F1EDE6',
  nude: '#E3D9CD',
  fendi: '#6E5B57',
  marsala: '#610027',
  marsalaSoft: 'rgba(97, 0, 39, 0.08)',
  marsalaBorder: 'rgba(97, 0, 39, 0.2)',
} as const;

// Logo branca outline pra usar no footer marsala · upload em S3.
const LOGO_WHITE_URL =
  'https://nexofacil-assets-prod.s3.amazonaws.com/brokers/deibiane/logo-white.png';

// Diferenciais do hero · trocou os selos genéricos ("Grátis · 2 minutos ·
// Sem compromisso") por proposições de valor mais comerciais a pedido
// da Deibiane.
const HERO_BADGES: Array<{ icon: LucideIcon; label: string }> = [
  { icon: TrendingUp, label: 'Descubra seu crédito' },
  { icon: Clock, label: 'Resultado em poucos minutos' },
  { icon: Heart, label: 'Atendimento personalizado' },
];

// Override dos 3 steps de "Como funciona" · copy específico da Deibiane
// (vs default MCMV genérico).
const HOW_IT_WORKS_STEPS: Array<{ title: string; body: string }> = [
  {
    title: 'Descubra o seu potencial de compra',
    body: 'São 4 etapas rápidas com perguntas sobre renda, FGTS e perfil de crédito.',
  },
  {
    title: 'Resultado em poucos minutos',
    body: 'Eu analiso suas respostas com base nas regras oficiais do MCMV.',
  },
  {
    title: 'Fale comigo no WhatsApp',
    body: 'Receba atendimento personalizado para conquistar seu imóvel com segurança.',
  },
];

// O imóvel marcado como destaque no painel ganha seção própria logo abaixo do
// hero (a API já manda ele primeiro na grade). Floratta tem a copy da campanha;
// qualquer outro usa os diferenciais cadastrados.
const CAMPAIGN_COPY: Record<
  string,
  { badge: string; eyebrow: string; highlights: Array<{ icon: LucideIcon; label: string }> }
> = {
  floratta: {
    badge: 'Pré-lançamento',
    eyebrow: 'Pré-lançamento',
    highlights: [
      { icon: Flame, label: 'Sacada com churrasqueira' },
      { icon: Car, label: '1 min da BR-101' },
      { icon: MapPin, label: 'Ao lado do Pagani' },
      { icon: Users, label: 'Renda a partir de R$ 5.500, podendo compor' },
    ],
  },
};

export function DeibianeLanding({ broker, properties, content, onCta }: LandingProps) {
  const featured = properties.find((p) => p.featured);

  return (
    <div className="min-h-screen" style={{ backgroundColor: DEIBIANE_COLORS.bgCream }}>
      <DeibianeHeader broker={broker} onCta={onCta} />
      <main>
        <Hero broker={broker} content={content} onCta={onCta} />
        {featured && <FeaturedLaunch property={featured} />}
        <HowItWorks />
        <AboutBroker broker={broker} content={content} />
        {/* O destaque já tem seção própria acima; a grade mostra o resto. */}
        {properties.some((p) => p !== featured) && (
          <PropertiesSection
            content={content}
            properties={properties.filter((p) => p !== featured)}
          />
        )}
        <FAQSection content={content} />
        <FinalCTA content={content} onCta={onCta} />
      </main>
      <DeibianeFooter broker={broker} content={content} />
    </div>
  );
}

// ============================================================================
// Header sticky · branco cremoso, brasão à esquerda, CTA marsala à direita
// ============================================================================

function DeibianeHeader({
  broker,
  onCta,
}: {
  broker: PublicBrokerSummary;
  onCta: () => void;
}) {
  return (
    <header
      className="sticky top-0 z-30 border-b backdrop-blur"
      style={{
        backgroundColor: 'rgba(241, 237, 230, 0.92)',
        borderColor: DEIBIANE_COLORS.marsalaBorder,
      }}
    >
      <div className="container flex h-20 items-center justify-between gap-3">
        {/* Foto + nome · padrão MCMV (mais pessoal que wordmark · cria
            conexão imediata com o lead). object-top evita cortar a face
            quando o crop circular tem aspect agressivo. */}
        <div className="flex items-center gap-3 min-w-0">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className="h-10 w-10 shrink-0 rounded-full object-cover"
              style={{
                objectPosition: 'center 25%',
                boxShadow: `0 0 0 2px ${DEIBIANE_COLORS.marsalaBorder}`,
              }}
            />
          ) : (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
              style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
            >
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="min-w-0 leading-tight">
            <div
              className="truncate font-serif text-base font-bold tracking-tight md:text-lg"
              style={{
                color: DEIBIANE_COLORS.marsala,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              {broker.name}
            </div>
            <div
              className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: DEIBIANE_COLORS.fendi }}
            >
              Especialista MCMV
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={onCta}
          className="h-11 shrink-0 px-4 text-sm font-semibold text-white hover:opacity-90"
          style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
        >
          Quero simular
        </Button>
      </div>
    </header>
  );
}

// ============================================================================
// Hero · split layout, copy serif esquerda + foto Deibiane direita
// ============================================================================

function Hero({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  const headline = broker.pageContent.heroHeadline ?? content.heroHeadlineDefault;
  const subheadline =
    broker.pageContent.heroSubheadline ?? content.heroSubheadlineDefault;
  // A foto profissional da broker é o avatar dela · `coverPhotoUrl` seria
  // uma foto de ambiente/local (a Deibiane não tem isso, ela é o foco).
  // Fallback pra coverPhotoUrl se algum dia ela quiser separar.
  const heroImage = broker.avatarUrl ?? broker.pageContent.coverPhotoUrl;

  return (
    <section className="relative overflow-hidden">
      <div className="container grid gap-12 py-14 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-16 md:py-24 lg:py-28">
        <div>
          {/* Prova mais forte dela: na primeira dobra, não 5 telas abaixo. */}
          <div
            className="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"
            style={{
              backgroundColor: DEIBIANE_COLORS.marsalaSoft,
              color: DEIBIANE_COLORS.marsala,
            }}
          >
            <Award className="h-3.5 w-3.5" />
            Top 3 Brasil · MRV 2026
          </div>
          <h1
            className="text-balance text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl lg:text-[3.75rem]"
            style={{
              color: DEIBIANE_COLORS.marsala,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            {headline}
          </h1>

          <p
            className="mt-6 max-w-xl text-balance text-base leading-relaxed md:text-lg"
            style={{ color: DEIBIANE_COLORS.fendi }}
          >
            {subheadline}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              onClick={onCta}
              className="h-12 px-7 text-base font-semibold text-white shadow-md hover:opacity-90"
              style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
            >
              Quero saber agora
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href="#empreendimentos"
              className="inline-flex h-12 items-center justify-center rounded-md px-4 text-sm font-medium underline-offset-4 transition-colors hover:underline"
              style={{ color: DEIBIANE_COLORS.fendi }}
            >
              Ver empreendimentos
            </a>
          </div>

          <ul className="mt-10 flex flex-col gap-2.5 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6">
            {HERO_BADGES.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="inline-flex items-center gap-2 font-medium"
                style={{ color: DEIBIANE_COLORS.fendi }}
              >
                <Icon
                  className="h-4 w-4 shrink-0"
                  style={{ color: DEIBIANE_COLORS.marsala }}
                />
                {label}
              </li>
            ))}
          </ul>
        </div>

        {heroImage ? (
          <div className="relative">
            {/* Foto profissional da broker com moldura nude sutil.
                object-position top pra não cortar a face em aspect 4:5 ·
                fotos com pose sentada (Deibiane) ficam centradas no peito
                por default, escondendo o queixo/cabelo. */}
            <div
              className="absolute -inset-4 rounded-3xl"
              style={{ backgroundColor: DEIBIANE_COLORS.nude }}
            />
            <img
              src={heroImage}
              alt={broker.name}
              className="relative aspect-[4/5] w-full rounded-3xl object-cover shadow-xl"
              style={{
                objectPosition: 'center top',
                boxShadow: `0 20px 40px -20px ${DEIBIANE_COLORS.marsalaBorder}`,
              }}
            />
          </div>
        ) : (
          <div
            className="aspect-[4/5] w-full rounded-3xl"
            style={{ backgroundColor: DEIBIANE_COLORS.nude }}
          />
        )}
      </div>
    </section>
  );
}

// ============================================================================
// Lançamento em destaque · foto grande + diferenciais + CTA que já leva o
// lead pro formulário do empreendimento (o lead chega no painel com o
// Floratta vinculado, não como lead genérico da página).
// ============================================================================

function FeaturedLaunch({ property }: { property: GetBrokerResponse['properties'][number] }) {
  const cover = property.photos[0];
  const campaign = CAMPAIGN_COPY[property.slug];
  const badge = campaign?.badge ?? (property.stage ? propertyStageLabel[property.stage] : null);
  const highlights =
    campaign?.highlights ??
    property.features.slice(0, 4).map((label) => ({ icon: Check, label }));
  return (
    <section className="pb-16 md:pb-24">
      <div className="container">
        <div
          className="grid overflow-hidden rounded-3xl bg-white shadow-lg md:grid-cols-[1.15fr_1fr]"
          style={{
            borderColor: DEIBIANE_COLORS.marsalaBorder,
            borderWidth: 1,
            boxShadow: `0 24px 48px -24px ${DEIBIANE_COLORS.marsalaBorder}`,
          }}
        >
          <Link to={`/empreendimento/${property.slug}`} className="relative block">
            {cover ? (
              <img
                src={cover}
                alt={property.name}
                className="h-full min-h-[240px] w-full object-cover md:min-h-[420px]"
              />
            ) : (
              <div className="h-full min-h-[240px]" style={{ backgroundColor: DEIBIANE_COLORS.nude }} />
            )}
            {badge && (
              <span
                className="absolute left-4 top-4 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white shadow-md"
                style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
              >
                {badge}
              </span>
            )}
          </Link>

          <div className="flex flex-col justify-center p-7 md:p-10">
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em]"
              style={{ color: DEIBIANE_COLORS.fendi }}
            >
              {campaign?.eyebrow ??
                (property.stage ? propertyStageLabel[property.stage] : 'Destaque')}{' '}
              em {property.city}
            </p>
            <h2
              className="mt-2 text-balance text-3xl font-bold leading-tight md:text-4xl"
              style={{
                color: DEIBIANE_COLORS.marsala,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              {property.name}
            </h2>
            {property.tagline && (
              <p className="mt-3 text-base leading-relaxed" style={{ color: DEIBIANE_COLORS.fendi }}>
                {property.tagline}
              </p>
            )}

            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {highlights.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-start gap-3 text-sm font-medium">
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: DEIBIANE_COLORS.marsalaSoft }}
                  >
                    <Icon className="h-4 w-4" style={{ color: DEIBIANE_COLORS.marsala }} />
                  </span>
                  <span className="pt-2" style={{ color: DEIBIANE_COLORS.fendi }}>
                    {label}
                  </span>
                </li>
              ))}
            </ul>

            <p
              className="mt-6 rounded-xl px-4 py-3 text-sm leading-relaxed"
              style={{ backgroundColor: DEIBIANE_COLORS.marsalaSoft, color: DEIBIANE_COLORS.marsala }}
            >
              {property.priceRange
                ? `${priceLabel(property)}. Faça sua análise gratuita e descubra se você consegue comprar.`
                : 'Os valores saem no lançamento. Quem deixa a análise pronta antes sai na frente e garante as melhores condições.'}
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                size="lg"
                className="h-12 px-6 text-base font-semibold text-white shadow-md hover:opacity-90"
                style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
              >
                <Link to={`/empreendimento/${property.slug}/qualificar`}>
                  Quero minha avaliação gratuita
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Link
                to={`/empreendimento/${property.slug}`}
                className="inline-flex h-12 items-center justify-center px-2 text-sm font-medium underline-offset-4 hover:underline"
                style={{ color: DEIBIANE_COLORS.fendi }}
              >
                Ver fotos e detalhes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// How it works · 3 passos numerados em serif
// ============================================================================

function HowItWorks() {
  return (
    <section
      className="border-y py-16 md:py-24"
      style={{
        backgroundColor: DEIBIANE_COLORS.nude,
        borderColor: DEIBIANE_COLORS.marsalaBorder,
      }}
    >
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <p
            className="text-xs font-semibold uppercase tracking-[0.2em]"
            style={{ color: DEIBIANE_COLORS.marsala }}
          >
            Como funciona
          </p>
          <h2
            className="mt-3 text-balance text-3xl font-bold leading-tight md:text-4xl"
            style={{
              color: DEIBIANE_COLORS.marsala,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            Em 3 passos simples
          </h2>
          <p
            className="mt-3 text-balance text-base"
            style={{ color: DEIBIANE_COLORS.fendi }}
          >
            Da simulação ao atendimento, tudo direto comigo · sem intermediários.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {HOW_IT_WORKS_STEPS.map((s, i) => (
            <div
              key={s.title}
              className="rounded-2xl bg-white p-7 shadow-sm"
              style={{ borderColor: DEIBIANE_COLORS.marsalaBorder, borderWidth: 1 }}
            >
              <div
                className="flex h-10 w-10 items-center justify-center rounded-full text-base font-bold text-white"
                style={{
                  backgroundColor: DEIBIANE_COLORS.marsala,
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                {i + 1}
              </div>
              <h3
                className="mt-5 text-xl font-bold leading-tight"
                style={{
                  color: DEIBIANE_COLORS.marsala,
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                {s.title}
              </h3>
              <p
                className="mt-2 text-sm leading-relaxed"
                style={{ color: DEIBIANE_COLORS.fendi }}
              >
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// About broker · "Quem vai te atender" · prova social premium
// ============================================================================

function AboutBroker({
  broker,
  content,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
}) {
  const about = broker.pageContent.aboutText ?? broker.bio;
  if (!about) return null;

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="grid gap-10 md:grid-cols-[320px_1fr] md:items-center md:gap-14">
          <div className="hidden md:block">
            {broker.avatarUrl ? (
              <div className="relative">
                <div
                  className="absolute -inset-3 rounded-3xl"
                  style={{ backgroundColor: DEIBIANE_COLORS.nude }}
                />
                <img
                  src={broker.avatarUrl}
                  alt={broker.name}
                  className="relative h-72 w-72 rounded-3xl object-cover shadow-lg md:h-80 md:w-80"
                  style={{ objectPosition: 'center top' }}
                />
              </div>
            ) : (
              <div
                className="flex h-72 w-72 items-center justify-center rounded-3xl text-7xl font-bold text-white"
                style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
              >
                {broker.name.charAt(0)}
              </div>
            )}
          </div>
          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em]"
              style={{ color: DEIBIANE_COLORS.marsala }}
            >
              {content.aboutTitle}
            </p>
            <h2
              className="mt-3 text-3xl font-bold leading-tight md:text-4xl"
              style={{
                color: DEIBIANE_COLORS.marsala,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              {broker.name}
            </h2>


            <div
              className="mt-6 whitespace-pre-line text-base leading-relaxed"
              style={{ color: DEIBIANE_COLORS.fendi }}
            >
              {about}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Properties · grid de empreendimentos (Flores de Bragança + Alvorada)
// ============================================================================

function PropertiesSection({
  content,
  properties,
}: {
  content: VerticalLandingContent;
  properties: GetBrokerResponse['properties'];
}) {
  const { filtered, filters } = usePropertyFilters(properties);
  return (
    <section
      id="empreendimentos"
      className="border-y py-16 md:py-24"
      style={{
        backgroundColor: DEIBIANE_COLORS.nude,
        borderColor: DEIBIANE_COLORS.marsalaBorder,
      }}
    >
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="text-balance text-3xl font-bold leading-tight md:text-4xl"
            style={{
              color: DEIBIANE_COLORS.marsala,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            {content.listingsTitle}
          </h2>
          <p
            className="mt-3 text-balance text-base"
            style={{ color: DEIBIANE_COLORS.fendi }}
          >
            Opções selecionadas pra famílias da Grande Florianópolis e região.
          </p>
        </div>

        <PropertyFilterBar
          filters={filters}
          accent={DEIBIANE_COLORS.marsala}
          className="mt-10 justify-center"
        />
        {filtered.length === 0 ? (
          <NoFilterMatch filters={filters} />
        ) : (
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// FAQ
// ============================================================================

function FAQSection({ content }: { content: VerticalLandingContent }) {
  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2
            className="text-balance text-3xl font-bold leading-tight md:text-4xl"
            style={{
              color: DEIBIANE_COLORS.marsala,
              fontFamily: '"Playfair Display", serif',
            }}
          >
            {content.faqTitle}
          </h2>
          <p
            className="mt-3 text-balance text-base"
            style={{ color: DEIBIANE_COLORS.fendi }}
          >
            {content.faqSubtitle}
          </p>
        </div>
        <div className="mx-auto mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {content.faqItems.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger
                  className="text-left text-base font-semibold"
                  style={{ color: DEIBIANE_COLORS.marsala }}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-base leading-relaxed"
                  style={{ color: DEIBIANE_COLORS.fendi }}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Final CTA · banner marsala forte
// ============================================================================

function FinalCTA({
  content,
  onCta,
}: {
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    <section
      className="relative overflow-hidden text-white"
      style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
    >
      <div className="container relative py-16 text-center md:py-24">
        <Sparkles className="mx-auto h-8 w-8" style={{ color: DEIBIANE_COLORS.nude }} />
        <h2
          className="mx-auto mt-4 max-w-2xl text-balance text-3xl font-bold leading-tight text-white md:text-4xl"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {content.finalCtaTitle}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-balance text-white/85">
          {content.finalCtaSubtitle}
        </p>
        <Button
          size="lg"
          onClick={onCta}
          className="mt-8 h-12 px-8 text-base font-semibold shadow-lg shadow-black/20 hover:opacity-90"
          style={{
            backgroundColor: DEIBIANE_COLORS.bgCream,
            color: DEIBIANE_COLORS.marsala,
          }}
        >
          {content.finalCtaButton}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </section>
  );
}

// ============================================================================
// Footer · marsala escuro com contato + Instagram + endereço
// ============================================================================

function DeibianeFooter({
  broker,
  content,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
}) {
  const instagram = broker.pageContent.socialLinks?.instagram;
  const address = broker.pageContent.address;
  const phone = broker.whatsappNumber;
  const year = new Date().getFullYear();

  return (
    <footer
      className="text-white"
      style={{ backgroundColor: DEIBIANE_COLORS.marsala }}
    >
      <div className="container py-12">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            {/* Logo nomativa branca outline (versão pra fundo escuro).
                Aspect ~2.7:1 depois do trim das margens · h-20 dá ~215px
                de largura, presença adequada sem dominar o footer. */}
            <img
              src={LOGO_WHITE_URL}
              alt="Deibiane Imóveis"
              className="h-20 w-auto"
              loading="lazy"
            />
            {broker.bio && (
              <p
                className="mt-5 max-w-md text-sm leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.75)' }}
              >
                {broker.bio}
              </p>
            )}
          </div>

          <div>
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em]"
              style={{ color: DEIBIANE_COLORS.nude }}
            >
              Contato
            </p>
            <div className="mt-4 space-y-2.5 text-sm">
              {/* Número como texto, sem link direto: o contato passa pelo
                  formulário (lead registrado e evento pra Meta). */}
              {phone && (
                <span className="inline-flex items-center gap-2 text-white/90">
                  <MessageCircle
                    className="h-4 w-4"
                    style={{ color: DEIBIANE_COLORS.nude }}
                  />
                  {formatPhoneDisplay(phone)}
                </span>
              )}
              {instagram && (
                <div>
                  <a
                    href={instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white/90 transition hover:text-white"
                  >
                    <Instagram
                      className="h-4 w-4"
                      style={{ color: DEIBIANE_COLORS.nude }}
                    />
                    @{instagram.replace(/\/+$/, '').split('/').pop()}
                  </a>
                </div>
              )}
              {address && (
                <div className="flex items-start gap-2 text-white/85">
                  <MapPin
                    className="mt-0.5 h-4 w-4 shrink-0"
                    style={{ color: DEIBIANE_COLORS.nude }}
                  />
                  <span className="whitespace-pre-line leading-relaxed">{address}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="mt-10 border-t pt-6"
          style={{ borderColor: 'rgba(255,255,255,0.15)' }}
        >
          <div className="flex flex-col items-center justify-between gap-3 text-xs sm:flex-row">
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>
              © {year} {broker.name} · {content.footerLabel}
            </span>
            <a
              href="/privacidade"
              className="underline transition-colors hover:text-white/80"
              style={{ color: 'rgba(255,255,255,0.6)' }}
            >
              Política de privacidade
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
